import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Badge, Button } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { formatDay, isOpen, planOrder, type ProjectDetail, type ProjectTask, taskTone } from './data';

/** Vista de lista del plan (guía 7.2): cada fase con sus pasos, en orden; quien gestiona agrega fases y pasos. */
export function ProjectPlan({ project }: { project: ProjectDetail }) {
  const ordered = planOrder(project.tasks);
  const phases = ordered.filter((x) => x.depth === 0);
  const childrenOf = (id: string) => ordered.filter((x) => x.parentId === id);

  if (phases.length === 0 && !project.can.manage) return <p className="text-sm text-slate-500">{t('pr.plan_vacio')}</p>;
  return (
    <div className="space-y-3">
      {phases.length === 0 && <p className="text-sm text-slate-500">{t('pr.plan_vacio')}</p>}
      <ol className="space-y-3" aria-label={t('pr.vista_lista')}>
        {phases.map((phase) => {
          const steps = childrenOf(phase.id);
          const done = steps.filter((s) => s.statusCategory === 'terminado').length;
          return (
            <li key={phase.id} className="rounded-lg border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2">
                <Link to={`/tareas/${phase.id}`} className="font-medium text-slate-900 hover:underline">
                  {phase.title}
                </Link>
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  {steps.length > 0 && t('pr.pasos_hechos', { hechos: done, total: steps.filter((s) => s.statusCategory !== 'cancelado').length })}
                  <TaskMeta task={phase} />
                </span>
              </div>
              {steps.length > 0 && (
                <ul className="divide-y divide-slate-100">
                  {steps.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2 pr-4" style={{ paddingLeft: `${1 + s.depth}rem` }}>
                      <Link to={`/tareas/${s.id}`} className={`text-sm hover:underline ${isOpen(s.statusCategory) ? 'text-slate-800' : 'text-slate-500 line-through'}`}>
                        {s.title}
                      </Link>
                      <TaskMeta task={s} />
                    </li>
                  ))}
                </ul>
              )}
              {project.can.manage && <AddTask projectId={project.id} parentId={phase.id} label={t('pr.agregar_paso')} />}
            </li>
          );
        })}
      </ol>
      {project.can.manage && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white">
          <AddTask projectId={project.id} parentId={null} label={t('pr.agregar_fase')} />
        </div>
      )}
    </div>
  );
}

function TaskMeta({ task }: { task: ProjectTask }) {
  return (
    <span className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      <Badge tone={taskTone(task.statusCategory)}>{task.status}</Badge>
      {task.assignees.length > 0 && <span>{task.assignees.map((a) => a.fullName).join(', ')}</span>}
      {task.dueDate && <span className={task.overdue ? 'font-medium text-red-700' : ''}>{task.overdue ? t('pr.vencio_el', { fecha: formatDay(task.dueDate) }) : t('pr.vence_el', { fecha: formatDay(task.dueDate) })}</span>}
    </span>
  );
}

/** Alta en línea de una fase (sin padre) o de un paso dentro de una fase. */
function AddTask({ projectId, parentId, label }: { projectId: string; parentId: string | null; label: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = `pr-nueva-${parentId ?? 'fase'}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/tareas', { json: { projectId, parentId, title: title.trim() } });
      setTitle('');
      await qc.invalidateQueries({ queryKey: ['proyectos'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="px-4 py-2">
        <Button variant="link" className="text-xs" onClick={() => setOpen(true)}>
          + {label}
        </Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-2 px-4 py-2">
      {error && <Alert>{error}</Alert>}
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <input
          id={id}
          autoFocus
          className="min-w-56 flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/30"
          placeholder={label}
          maxLength={300}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Button type="submit" className="px-3 py-1" disabled={busy || !title.trim()}>
          {t('pr.agregar')}
        </Button>
        <Button variant="secondary" className="px-3 py-1" onClick={() => setOpen(false)}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}
